import { CacheStrategy, DataSourceStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { LIFECYCLE_FIELD_DESCRIPTORS } from './_lifecycleFields.js';
import { SYSTEM_ALLOWED_DATA_SOURCES } from './dataSourcePolicy.js';
import { PROFILE_DATA, USER_PROFILE } from '../types/profile.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for ProfileData entity.
 * Stored in 'org/{orgId}/profile'.
 *
 * Fields without groups are always persisted.
 * Fields with groups are persisted only when those groups are active.
 */
export const profileDataDescriptor = {
    name: PROFILE_DATA,
    label: 'Perfil',
    domain: 'identity',
    collectionPath: 'org/{orgId}/profile',
    pathPatternKeys: {
        orgId: 'string',
    },
    version: 1,
    description: 'Represents a user profile within an organization',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
            // No groups = always persisted
            description: 'Unique profile identifier',
        },
        {
            name: 'name',
            label: 'Nome',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.NAME,
            description: 'Profile name',
        },
        {
            name: 'abbreviation',
            label: 'Abreviação',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.ABBREVIATION,
            description: 'Short abbreviation for the profile',
        },
        {
            name: 'userId',
            label: 'ID do usuário dono',
            type: 'string',
            required: false,
            description: 'If set, this is a user-owned personal profile',
        },
        {
            name: 'grants',
            label: 'Concessões de Módulos',
            type: 'array',
            arrayItemType: 'object',
            required: false,
            description: 'Module grants (group / module / deny). Spec 0038 §3.4',
        },
        {
            name: 'flatModules',
            label: 'Listar módulos sem grupos',
            type: 'boolean',
            required: false,
            description: 'Lista os módulos concedidos sem os grupos — só apresentação; grants não mudam',
        },
        ...LIFECYCLE_FIELD_DESCRIPTORS,
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'userId',
            targetEntity: 'UserData',
            relationship: 'one-to-one',
            description: 'The user who owns this profile (if user-specific)',
            // Label-only (0167 §11) — same reason as the audit user FKs.
            expand: false,
        },
    ],
};
/**
 * Descriptor for UserProfile entity.
 * Stored in 'org/{orgId}/users/{userId}/profiles'.
 * Represents the link between a user and a profile.
 */
export const userProfileDescriptor = {
    name: USER_PROFILE,
    label: 'Vínculo usuário–perfil',
    domain: 'identity',
    collectionPath: 'org/{orgId}/users/{userId}/profiles',
    pathPatternKeys: {
        orgId: 'string',
        userId: 'string',
    },
    version: 1,
    description: 'Links a user to a profile within an organization',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
            // No groups = always persisted
            description: 'Link identifier',
        },
        {
            name: 'profileId',
            label: 'ID do perfil',
            type: 'string',
            required: true,
            description: 'Reference to the linked profile',
        },
    ],
    foreignKeys: [
        {
            fieldName: 'profileId',
            targetEntity: 'ProfileData',
            relationship: 'one-to-one',
            description: 'The profile this link points to',
        },
    ],
};
//# sourceMappingURL=profile.descriptor.js.map